import { JSONObject, JSONValue } from '../typings/json';
import { camelCase, snakeCase } from './string';

type Input = Omit<JSONObject, 'null'>;
type TransformedOutput = Input | TransformedOutput[];
type UnknownInput = Input | Input[];
type Transformer = (key: string) => string;

export const isObject = (input: UnknownInput) =>
  typeof input === 'object' && input !== null;

const makeReducer =
  (transformer: Transformer) =>
  (acc: Record<string, JSONValue>, [key, value]: [string, UnknownInput]) => {
    const transformedKey = transformer(key);
    if (isObject(value)) {
      acc[transformedKey] = Array.isArray(value)
        ? value.map(x => transformKeys(x, transformer))
        : transformKeys(value, transformer);
      return acc;
    }
    acc[transformedKey] = value;
    return acc;
  };

const transformKeys = (
  input: UnknownInput,
  transformer: Transformer,
): TransformedOutput => {
  if (Array.isArray(input))
    return input.map(x => transformKeys(x, transformer));
  if (isObject(input)) {
    const reducer = makeReducer(transformer);
    return Object.entries(input as object).reduce(
      reducer,
      {} as Record<string, JSONValue>,
    );
  }
  return input;
};

export const camelizeKeys = (input: UnknownInput): TransformedOutput =>
  transformKeys(input, camelCase);

export const decamelizeKeys = (input: UnknownInput): TransformedOutput =>
  transformKeys(input, snakeCase);
